<template>
    <loading-indicator v-if="entries === null" text="Liste wird geladen"/>
    <div v-else data-list-container :id="'list-' + _uid" aria-label="Seitenauswahl Liste">
        <slot v-bind="defaultSlotData"/>

        <slot name="pager" v-if="!smallDesign">
            <div class="list-bottom no-print" v-if="showEntryCount || (showPager && (filteredCount || filteredEntries.length) > availablePageSizes[0])">
                <div v-if="showEntryCount" class="list-bottom-left">
                    <span v-text="pagerText"></span>
                </div>

                <div class="list-bottom-right" v-if="showPager && (filteredCount || filteredEntries.length) > availablePageSizes[0]">
                    <div class="page-size" aria-label="Einträge pro Seite wählen">
                        <div>
                            <div class="page-size-info">Einträge</div>
                        </div>
                        <ul class="page-size-select flex">
                            <li v-for="size in availablePageSizes">
                                <button v-interact="() => setSize(size)" :disabled="size === _entriesPerPage" :aria-label="size + ' Einträge pro Seite anzeigen'"
                                    class="button button-default pager-tweak" :value="size" v-text="size">
                                </button>
                            </li>
                        </ul>
                    </div>
                    <nav class="pager" aria-label="Seitennavigation">
                        <ul class="flex">
                            <li>
                                <button v-interact="() => setPage(1)" :disabled="page === 1" type="submit" class="button button-default pager-tweak" aria-label="Zur ersten Seite">
                                    <i class="material-icons md-18" aria-hidden="true" role="img">first_page</i>
                                </button>
                            </li>
                            <li>
                                <button v-interact="() => setPage(page - 1)" :disabled="page === 1" type="submit" class="button button-default pager-tweak"
                                    :aria-label="'Zur vorherigen Seite (' + (page - 1) + ' von ' + pages + ')'">
                                    <i class="material-icons md-18" aria-hidden="true" role="img">chevron_left</i>
                                </button>
                            </li>
                            <li>
                                <div class="pager-input">
                                    <input v-debounce="setPage" :value="page" type="number" min="1" :max="pages" class="button button-default pager-tweak page-input"
                                        aria-label="Seitenangabe">
                                </div>
                            </li>
                            <li class="pager-input">
                                <span>von {{pages}}</span>
                            </li>
                            <li>
                                <button v-interact="() => setPage(page + 1)" :disabled="page === pages" type="submit" class="button button-default pager-tweak"
                                    :aria-label="'Zur nächsten Seite (' + (page + 1) + ' von ' + pages + ')'">
                                    <i class="material-icons md-18" aria-hidden="true" role="img">chevron_right</i>
                                </button>
                            </li>
                            <li>
                                <button v-interact="() => setPage(pages)" :disabled="page === pages" type="submit" class="button button-default pager-tweak"
                                    :aria-label="'Zur letzten Seite (' + pages + ')'">
                                    <i class="material-icons md-18" aria-hidden="true" role="img">last_page</i>
                                </button>
                            </li>
                            <li>
                                <a :href="'#list-' + _uid" title="Zum Listenanfang" class="screen-reader-only-focusable skip-navigation-link">Zum Listenanfang</a>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div class="cleaner"></div>
            </div>
        </slot>

        <slot name="pager-small" v-if="smallDesign">
            <div class="list-bottom no-print" v-if="showEntryCount || (showPager && (filteredCount || filteredEntries.length) > availablePageSizes[0])">
                <div class="list-bottom-center" v-if="showPager && (filteredCount || filteredEntries.length) > availablePageSizes[0]">
                    <ul class="pager flex-center">
                        <li>
                            <button v-interact="() => setPage(page - 10)" :disabled="page === 1" type="submit" class="button button-default pager-tweak" :aria-label="'Seitenauswahl zurück zur Seite ' + parsePage(page - 10) + ' von ' + pages">
                                <i class="material-icons md-18" aria-hidden="true" role="img">fast_rewind</i>
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(1)" v-if="page > 1" type="submit" class="button button-default pager-tweak" aria-label="Zur ersten Seite">
                                1
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page - 3)" v-if="page > 4 && page + 2 > pages" type="submit" class="button button-default pager-tweak" :aria-label="'Zurück zur Seite ' + parsePage(page - 3) + ' von ' + pages">
                                {{page - 3}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page - 2)" v-if="page > 3 && page + 1 > pages" type="submit" class="button button-default pager-tweak" :aria-label="'Zurück zur Seite ' + parsePage(page - 2) + ' von ' + pages">
                                {{page - 2}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page - 1)" v-if="page > 2" type="submit" class="button button-default pager-tweak" :aria-label="'Zurück zur Seite ' + parsePage(page - 1) + ' von ' + pages">
                                {{page - 1}}
                            </button>
                        </li>
                        <li>
                            <button class="button button-default pager-tweak cursor-default pager-current" aria-current="true" :aria-label="'Aktuelle Seite, ' + page + ' von ' + pages">
                                {{page}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page + 1)" v-if="page + 1 < pages" type="submit" class="button button-default pager-tweak" :aria-label="'Weiter zur Seite ' + parsePage(page + 1) + ' von ' + pages">
                                {{page + 1}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page + 2)" v-if="page <= 2 && page + 2 < pages" type="submit" class="button button-default pager-tweak" :aria-label="'Weiter zur  Seite ' + parsePage(page + 2) + ' von ' + pages">
                                {{page + 2}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page + 3)" v-if="page <= 1 && page + 3 < pages" type="submit" class="button button-default pager-tweak" :aria-label="'Weiter zur  Seite ' + parsePage(page + 3) + ' von ' + pages">
                                {{page + 3}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(pages)" v-if="page < pages && pages > 1" type="submit" class="button button-default pager-tweak" :aria-label="'Zur letzten Seite, ' + pages">
                                {{pages}}
                            </button>
                        </li>
                        <li>
                            <button v-interact="() => setPage(page + 10)" :disabled="page === pages" type="submit" class="button button-default pager-tweak" :aria-label="'Seitenauswahl weiter zur Seite ' + parsePage(page + 10) + ' von ' + pages">
                                <i class="material-icons md-18" aria-hidden="true" role="img">fast_forward</i>
                            </button>
                        </li>
                        <li>
                            <a :href="'#list-' + _uid" title="Zum Listenanfang" class="screen-reader-only-focusable skip-navigation-link">Zum Listenanfang</a>
                        </li>
                    </ul>
                </div>
                <div v-if="showEntryCount" class="list-bottom-center">
                    <span v-text="pagerText"></span>
                </div>
            </div>
        </slot>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Watch} from 'vue-facing-decorator';
    import LocalStorage from '../library/localStorage';
    import MediaQueryHandler from '../library/mediaQueryHandler';
    import ListAbstract from '../mixins/ListAbstract';
    import LoadingIndicator from './LoadingIndicator.vue';

    @Component({
        name: 'ListPager',
        components: {LoadingIndicator},
    })
    export default class ListPager extends ListAbstract
    {
        @Prop({
            type: [String, Number],
        }) identifier: string | number;

        @Prop({
            type: [Array],
            default()
            {
                return [25, 50, 100, 200];
            },
        }) availablePageSizes: number[];

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) showEntryCount: boolean;

        @Prop({
            type: String,
            default: 'Eintrag',
            required: false,
        }) singleEntries: string;

        @Prop({
            type: String,
            default: 'Einträge',
            required: false,
        }) multipleEntries: string;

        @Prop({
            type: String,
            default: 'verfügbar',
            required: false,
        }) entriesAvailable: string;

        @Prop({
            type: Function,
            required: true,
        }) setSort: SetSortCallable;

        @Prop({
            type: [String, Boolean],
            required: true,
        }) currentScope: string | false;

        smallDesign = false;

        @Watch('page')
        watchPage()
        {
            this.updatePageLocalStorage();
        }

        mounted()
        {
            MediaQueryHandler.on('screen-sm-down',
                {
                    match: this.setSmallDesign,
                    unmatch: this.unsetSmallDesign,
                },
            );
        }

        setSmallDesign()
        {
            this.smallDesign = true;
        }

        unsetSmallDesign()
        {
            this.smallDesign = false;
        }

        @Watch('lastReload')
        rebuildOnLastChange()
        {
            if (this.initializeLoading)
            {
                return;
            }

            if ('server' === this.filterMethod)
            {
                this.fetchList();
            }
        }

        get filterObj()
        {
            return this.getFilterObject();
        }

        get listEntries()
        {
            if ('server' === this.filterMethod)
            {
                return this.entries;
            }

            if (!this.showPager)
            {
                return this.sortedEntries;
            }

            return this.sortedEntries.slice(this._entriesPerPage * (this.page - 1), this._entriesPerPage * (this.page));
        }

        get pagerText()
        {
            let count      = this.filteredCount || this.filteredEntries.length,
                totalCount = this.totalCount || this.entries.length || count;

            return `${count}
            ${!this.smallDesign && totalCount !== count ? 'von ' + totalCount : ''}
            ${1 === count ? this.singleEntries : this.multipleEntries}
            ${this.entriesAvailable}`;
        }

        get pagerCount()
        {
            return this.filteredCount || this.filteredEntries.length;
        }

        get pagerTotalCount()
        {
            return this.totalCount || this.entries.length || this.pagerCount;
        }

        get pages()
        {
            let pages = Math.ceil((this.filteredCount || this.filteredEntries.length) / this._entriesPerPage);
            pages     = Math.max(1, pages);

            let localStoragePage = this.getPageLocalStorage();
            if(this.page !== localStoragePage && localStoragePage <= pages)
            {
                this.page = localStoragePage;
                this.rebuild(false, false);
            }
            else if (this.page < 1 || this.page > pages)
            {
                this.rebuild(false, true);
            }

            return pages;
        }

        get _entriesPerPage()
        {
            return this.smallDesign ? this.availablePageSizes[0] : this.entriesPerPage;
        }

        get defaultSlotData()
        {
            return {
                entriesCount: this.entriesCount,
                filteredEntries: this.filteredEntries,
                listEntries: this.listEntries,
                loading: this.initializeLoading || this.loading,
                orderBy: this.orderBy,
                setSort: this.setSort,
                sortField: this.sortField,
                sortedEntries: this.sortedEntries,
                totalCount: this.pagerTotalCount,
            };
        }

        setSize(size: number)
        {
            this.entriesPerPage = size;
            this.rebuild();
        }

        setPage(page: string | number, event?: KeyboardEvent)
        {
            if (event instanceof KeyboardEvent && isNaN(<number><unknown>event.key))
            {
                return;
            }

            page = this.parsePage(page);

            // nothing changed, prevent rebuild
            if (this.page === page)
            {
                return;
            }

            this.page = page;
            this.rebuild(false, false);
        }

        parsePage(page: string | number): number
        {
            if ('number' !== typeof page)
            {
                page = parseInt(page);
            }

            if (page < 1)
            {
                page = 1;
            }
            else if (page > this.pages)
            {
                page = this.pages;
            }

            return page;
        }

        getPageLocalStorage()
        {
            return LocalStorage.get('page.' + this.currentScope, this.page);
        }

        updatePageLocalStorage()
        {
            LocalStorage.set('page.' + this.currentScope, this.page);
        }
    }
</script>